import React from 'react';
import { UserMinus, UserPlus } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { Box, Text } from '@gluestack-ui/themed';
import { UserLifecycleState, type CourseStudent } from '@/common/api/models';
import type { ReferenceListValue } from '@/admin/common/references';
import { StatusBadge } from '@/admin/common/StatusBadge';
import { MembershipControl } from '@/admin/common/MembershipControl';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import type { ColumnDefinition } from '@/admin/common/DataColumnConfig';
import EnrollmentExtraModulesField from './EnrollmentExtraModulesField';

interface CourseEnrollmentsFormProps {
  route: {
    params: {
      data?: {
        id: string;
      };
    };
  };
}

/**
 * Column definitions for enrollment table display.
 * Extracted as typed constant to ensure proper type inference in render functions.
 *
 * Pattern: Extract columns outside component to allow TypeScript to correctly
 * infer the row type as CourseStudent in all render functions.
 */
const enrollmentColumns: Record<string, ColumnDefinition<CourseStudent>> = {
  'cohort.title': {
    label: 'Cohort',
    width: 150,
    type: 'string',
    sortable: true,
  },
  lastname: {
    label: 'Student',
    width: 200,
    type: 'string',
    sortable: true,
    render: (row) => (
      <Text size="xs">{`${row.lastname}, ${row.firstname}`}</Text>
    ),
  },
  email: {
    label: 'Email',
    width: 250,
    type: 'string',
    sortable: true,
  },
  state: {
    label: 'Status',
    width: 100,
    type: 'string',
    render: (row) => (
      <StatusBadge value={row.state || 'active'} />
    ),
  },
  orderId: {
    label: 'Order ID',
    width: 120,
    type: 'string',
    sortable: true,
  },
  enrollmentDate: {
    label: 'Enrolled At',
    width: 120,
    type: 'date',
    sortable: true,
  },
  extraModuleIds: {
    label: 'Extra modules',
    width: 110,
    type: 'string',
    render: (row) => {
      const count = row.extraModuleIds?.length ?? 0;
      return (
        <Text size="xs" color={count ? '$textBody' : '$textSubtle'}>
          {count || '—'}
        </Text>
      );
    },
  },
};

/**
 * Columns for add student modal (subset of full columns)
 */
const enrollmentAddColumns: Record<string, ColumnDefinition<CourseStudent>> = {
  'cohort.title': {
    label: 'Cohort',
    width: 150,
    type: 'string',
    sortable: true,
  },
  lastname: {
    label: 'Student',
    width: 200,
    type: 'string',
    sortable: true,
    render: (row) => (
      <Text size="xs">{`${row.lastname}, ${row.firstname}`}</Text>
    ),
  },
  email: {
    label: 'Email',
    width: 250,
    type: 'string',
    sortable: true,
  },
  state: {
    label: 'Status',
    width: 100,
    type: 'string',
    render: (row) => (
      <StatusBadge value={row.state || 'active'} />
    ),
  },
};

/**
 * Column definitions for edit modal (form fields only)
 */
const enrollmentEditColumns = {
  orderId: {
    label: 'Order ID',
    width: 250,
    fieldWidth: '100%',
    type: 'string' as const,
  },
  enrollmentDate: {
    label: 'Enrollment Date',
    width: 250,
    fieldWidth: '100%',
    type: 'date' as const,
    placeholder: 'YYYY-MM-DD',
    transform: {
      toForm: (val: string) => {
        if (!val) return '';
        return val.split('T')[0];
      },
      fromForm: (val: string) => {
        if (!val) return null;
        try {
          if (val.includes('T')) return val;
          const date = new Date(val);
          if (Number.isNaN(date.getTime())) return val;
          return date.toISOString();
        } catch (e) {
          return val;
        }
      },
    },
  },
  state: {
    label: 'Status',
    type: 'list' as const,
    fieldWidth: '100%',
    choices: Object.values(UserLifecycleState).map((s) => ({
      label: s.charAt(0).toUpperCase() + s.slice(1),
      value: s,
    })),
  },
  extraModuleIds: {
    label: 'Extra modules',
    type: 'custom' as const,
    component: EnrollmentExtraModulesField,
    transform: {
      toForm: (val: string[] | null | undefined, row: unknown) => {
        const enrollment = row as CourseStudent;
        if (enrollment.extraModules?.length) {
          return enrollment.extraModules.flatMap((item) => (
            item.id ? [{ id: item.id, title: item.title ?? undefined }] : []
          ));
        }
        return (val ?? []).map((id) => ({ id }));
      },
      fromForm: (val: ReferenceListValue | null | undefined) => {
        if (!Array.isArray(val) || val.length === 0) return null;
        return val.map((item) => item.id);
      },
    },
  },
};

/** Exported for unit tests (form saves ids only). */
export const enrollmentExtraModuleIdsTransform = enrollmentEditColumns.extraModuleIds.transform;

const CourseEnrollmentsForm: React.FC<CourseEnrollmentsFormProps> = ({ route }) => {
  const { t } = useTranslation();
  const { reportApiError } = useApiErrorReporter();
  const courseId = route.params.data?.id;

  const editModalColumns = React.useMemo(() => enrollmentEditColumns, []);

  if (!courseId) return null;

  return (
    <Box px="$4" flex={1}>
      <MembershipControl<'users', CourseStudent>
        parentObjectName="courses"
        parentId={courseId}
        objectName="users"
        noPadding
        noTitle
        label="Enrolled Students"
        icons={{ add: UserPlus, delete: UserMinus }}
        addMemberFilter={{ role: 'student' }}
        excludeCurrent
        doubleConfirmUnlink
        unlinkConfirmationTitle={t('errors.membership.remove_student_title', { defaultValue: 'Remove Student from Course' })}
        unlinkConfirmationMessage={t('errors.membership.remove_student_message', { defaultValue: 'Removing a student from this course will result in the loss of all their progress, tasks and grades associated with this course. This action cannot be undone.' })}
        columns={enrollmentColumns}
        addModalColumns={enrollmentAddColumns}
        editModalColumns={editModalColumns}
        defaultSort="lastname"
        heightsOverrides={{ header: 260 }}
        pageSize={10}
        extraActions={{
          bulkDelete: {
            label: t('errors.membership.bulk_remove_students_title', { defaultValue: 'Remove Selected Students' }),
            icon: UserMinus,
            bulk: true,
          },
        }}
        onLinkError={(error) => reportApiError(error, t('admin.course.enrollments.errors.addStudent', { defaultValue: 'Add Student' }))}
        defaultSearchField="lastname"
        searchableFields={['lastname', 'firstname', 'email', 'username', 'id', 'orderId']}
      />
    </Box>
  );
};

export default CourseEnrollmentsForm;
